import {
  Entity,
  RELATION_OWNED_BY,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import {
  ItemCardHeader,
  Link,
  MarkdownContent,
  UserIcon,
} from '@backstage/core-components';
import { useAnalytics, useApp, useRouteRef } from '@backstage/core-plugin-api';
import { useTranslationRef } from '@backstage/frontend-plugin-api';
import {
  EntityRefLinks,
  FavoriteEntity,
  entityRouteParams,
  entityRouteRef,
  getEntityRelations,
} from '@backstage/plugin-catalog-react';
import { usePermission } from '@backstage/plugin-permission-react';
import { taskCreatePermission } from '@backstage/plugin-scaffolder-common/alpha';
import { scaffolderReactTranslationRef } from '@backstage/plugin-scaffolder-react';
import { TemplateCard } from '@backstage/plugin-scaffolder-react/alpha';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CardActions from '@material-ui/core/CardActions';
import CardContent from '@material-ui/core/CardContent';
import Chip from '@material-ui/core/Chip';
import Divider from '@material-ui/core/Divider';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import DescriptionIcon from '@material-ui/icons/Description';
import LanguageIcon from '@material-ui/icons/Language';
import { ComponentType, useCallback } from 'react';

const ICON_ANNOTATION = 'agentic-factory.totalenergies.io/icon';

// Frontal panda glyph, monochrome, inheriting the header font colour. Pupils
// are evenodd cut-outs, so nothing white is painted and it reads on any ground.
const PandaMark = ({ size = 40 }: { size?: number }) => (
  <svg
    viewBox="0 0 100 100"
    role="img"
    aria-label="PANDA"
    style={{ width: size, height: size, display: 'block', color: 'inherit' }}
  >
    <circle cx="24" cy="24" r="13" fill="currentColor" />
    <circle cx="76" cy="24" r="13" fill="currentColor" />
    <circle
      cx="50"
      cy="52"
      r="33"
      fill="none"
      stroke="currentColor"
      strokeWidth="7"
    />
    <path
      fillRule="evenodd"
      transform="rotate(-18 37 50)"
      d="M26,50 a11,15 0 1,0 22,0 a11,15 0 1,0 -22,0 z M33,47 a4,4 0 1,0 8,0 a4,4 0 1,0 -8,0 z"
      fill="currentColor"
    />
    <path
      fillRule="evenodd"
      transform="rotate(18 63 50)"
      d="M52,50 a11,15 0 1,0 22,0 a11,15 0 1,0 -22,0 z M59,47 a4,4 0 1,0 8,0 a4,4 0 1,0 -8,0 z"
      fill="currentColor"
    />
    <ellipse cx="50" cy="65" rx="5" ry="3.6" fill="currentColor" />
    <path
      d="M44,71 Q50,76 56,71"
      stroke="currentColor"
      strokeWidth="3"
      fill="none"
      strokeLinecap="round"
    />
  </svg>
);

// Delivery metrics over a lake: ascending bars on a waterline. Monochrome and
// currentColor for the same reason as the panda above.
const DevlakeMark = ({ size = 40 }: { size?: number }) => (
  <svg
    viewBox="0 0 100 100"
    role="img"
    aria-label="DevLake"
    style={{ width: size, height: size, display: 'block', color: 'inherit' }}
  >
    <rect x="20" y="46" width="14" height="26" rx="2" fill="currentColor" />
    <rect x="43" y="32" width="14" height="40" rx="2" fill="currentColor" />
    <rect x="66" y="20" width="14" height="52" rx="2" fill="currentColor" />
    <path
      d="M12,82 q12,-8 24,0 t24,0 t24,0"
      stroke="currentColor"
      strokeWidth="6"
      fill="none"
      strokeLinecap="round"
    />
  </svg>
);

// A product ships its glyph here and opts in from its template with
// `agentic-factory.totalenergies.io/icon: <key>`. No key, no mark. Glyphs must
// be monochrome currentColor: they inherit the header font colour.
const MARKS: Record<string, ComponentType<{ size?: number }>> = {
  panda: PandaMark,
  devlake: DevlakeMark,
};

const markFor = (template?: Entity) => {
  const key = template?.metadata?.annotations?.[ICON_ANNOTATION];
  return key ? MARKS[key] : undefined;
};

const useStyles = makeStyles(theme => ({
  header: {
    backgroundImage: ({ backgroundImage }: any) => backgroundImage,
    color: ({ fontColor }: any) => fontColor,
  },
  subtitle: { display: 'flex', justifyContent: 'space-between' },
  detailIcon: { color: ({ fontColor }: any) => fontColor },
  title: { display: 'flex', alignItems: 'center', gap: theme.spacing(1.25) },
  description: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    '-webkit-line-clamp': 10,
    '-webkit-box-orient': 'vertical',
  },
  markdown: { '& :first-child': { margin: 0 } },
  linkText: { display: 'inline-flex', alignItems: 'center' },
  actionContainer: { padding: '16px', flex: 1, alignItems: 'flex-end' },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    flex: 1,
    alignItems: 'center',
  },
  ownedBy: {
    display: 'flex',
    alignItems: 'center',
    flex: 1,
    color: theme.palette.link,
  },
}));

const CardLink = ({
  icon: Icon,
  text,
  url,
  className,
}: {
  icon: ComponentType<any>;
  text?: string;
  url: string;
  className: string;
}) => (
  <div className={className}>
    <Icon fontSize="small" />
    <Link style={{ marginLeft: '8px' }} to={url}>
      {text || url}
    </Link>
  </div>
);

// Rebuilt scaffolder template card. TemplateCard is a swappable whose default
// implementation is not exported, so the card is reconstructed from public
// APIs, mirroring the upstream one feature for feature (favourite, detail link,
// markdown description, tags, links, owner refs, permission-gated Choose). The
// only addition is the leading product mark in the header.
export const ProductTemplateCard = (
  props: React.ComponentProps<typeof TemplateCard>,
) => {
  const { template, additionalLinks, onSelected } = props as {
    template: Entity;
    additionalLinks?: {
      icon?: ComponentType<any>;
      text: string;
      url: string;
    }[];
    onSelected?: () => void;
  };

  const app = useApp();
  const analytics = useAnalytics();
  const { t } = useTranslationRef(scaffolderReactTranslationRef);
  const catalogEntityRoute = useRouteRef(entityRouteRef);
  const { allowed: canCreateTask } = usePermission({
    permission: taskCreatePermission,
  });

  const type = (template.spec as { type?: string } | undefined)?.type;
  const pageTheme = (useTheme() as any).getPageTheme({ themeId: type });
  const styles = useStyles({
    backgroundImage: pageTheme.backgroundImage,
    fontColor: pageTheme.fontColor,
  });

  const Mark = markFor(template);
  const entityRef = stringifyEntityRef(template);
  const ownedByRelations = getEntityRelations(template, RELATION_OWNED_BY);
  const hasTags = !!template.metadata.tags?.length;
  const hasLinks =
    !!additionalLinks?.length || !!template.metadata.links?.length;
  const TemplateIcon = app.getSystemIcon('kind:template') || DescriptionIcon;
  const iconResolver = (key?: string) =>
    key ? app.getSystemIcon(key) ?? LanguageIcon : LanguageIcon;

  const handleChoose = useCallback(() => {
    analytics.captureEvent('click', 'Template has been opened');
    onSelected?.();
  }, [analytics, onSelected]);

  return (
    <Card>
      <ItemCardHeader
        classes={{ root: styles.header }}
        title={
          Mark ? (
            <span className={styles.title}>
              <Mark size={30} />
              <span>{template.metadata.title ?? template.metadata.name}</span>
            </span>
          ) : (
            template.metadata.title ?? template.metadata.name
          )
        }
        subtitle={
          <div className={styles.subtitle}>
            <div>{type}</div>
            <div>
              <Tooltip
                id={`tooltip-${entityRef}`}
                title={t('cardHeader.detailBtnTitle')}
              >
                <IconButton
                  aria-label={t('cardHeader.detailBtnTitle')}
                  id={`viewDetail-${entityRef}`}
                  style={{ padding: 0 }}
                >
                  <Typography component="span">
                    <Link
                      to={catalogEntityRoute(entityRouteParams(template))}
                      style={{ display: 'flex', alignItems: 'center' }}
                    >
                      <TemplateIcon className={styles.detailIcon} />
                    </Link>
                  </Typography>
                </IconButton>
              </Tooltip>
              <FavoriteEntity
                entity={template}
                style={{ padding: 0, marginLeft: 6 }}
              />
            </div>
          </div>
        }
      />

      <CardContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box className={styles.description}>
              <MarkdownContent
                className={styles.markdown}
                content={
                  template.metadata.description ??
                  t('templateCard.noDescription')
                }
              />
            </Box>
          </Grid>

          {!hasTags && !hasLinks && (
            <Grid item xs={12}>
              <Divider />
            </Grid>
          )}

          {hasTags && (
            <>
              <Grid item xs={12}>
                <Divider />
              </Grid>
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  {template.metadata.tags!.map(tag => (
                    <Grid item key={`grid-${tag}`}>
                      <Chip style={{ margin: 0 }} size="small" label={tag} />
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </>
          )}

          {hasLinks && (
            <>
              <Grid item xs={12}>
                <Divider />
              </Grid>
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  {additionalLinks?.map(({ icon, text, url }, index) => (
                    <Grid item xs={6} key={`al-${index}`}>
                      <CardLink
                        className={styles.linkText}
                        icon={icon ?? LanguageIcon}
                        text={text}
                        url={url}
                      />
                    </Grid>
                  ))}
                  {template.metadata.links?.map(
                    ({ url, icon, title }, index) => (
                      <Grid item xs={6} key={`ml-${index}`}>
                        <CardLink
                          className={styles.linkText}
                          icon={iconResolver(icon)}
                          text={title || url}
                          url={url}
                        />
                      </Grid>
                    ),
                  )}
                </Grid>
              </Grid>
            </>
          )}
        </Grid>
      </CardContent>

      <CardActions className={styles.actionContainer}>
        <div className={styles.footer}>
          <div className={styles.ownedBy}>
            {ownedByRelations.length > 0 && (
              <>
                <UserIcon fontSize="small" />
                <EntityRefLinks
                  style={{ marginLeft: '8px' }}
                  entityRefs={ownedByRelations}
                  defaultKind="Group"
                  hideIcons
                />
              </>
            )}
          </div>
          {canCreateTask ? (
            <Button
              size="small"
              variant="outlined"
              color="primary"
              onClick={handleChoose}
            >
              {t('templateCard.chooseButtonText')}
            </Button>
          ) : null}
        </div>
      </CardActions>
    </Card>
  );
};
